import { memo, useMemo } from "react";
import { Modal, Image, Col, Row } from "react-bootstrap";
import { User } from "../../types/Types";
import "../css/ProfileModal.css";
import { MdOutlineBusiness } from "react-icons/md";
import {
  FaBuilding,
  FaBullseye,
  FaGlobe,
  FaGraduationCap,
  FaIdBadge,
  FaIndustry,
  FaInfoCircle,
  FaLink,
  FaMapMarkerAlt,
  FaTools,
  FaUniversity,
  FaUserCheck,
  FaUserCircle,
  FaUsers,
} from "react-icons/fa";
import CurvedTextOverlay from "../Account/CurvedTextOverlay";
import { useMediaQuery } from "react-responsive";

interface ProfileModalProps {
  show: boolean;
  onHide: () => void;
  selectedUser: User | null;
  title?: string;
  isConnected?: boolean;
}

const DEFAULT_TITLE = "Profile";

const IMAGE_SIZES = { desktop: 150, lgTablet: 140, mdTablet: 130, mobile: 95 } as const;

function getMembershipTheme(membershipName: string | undefined) {
  switch (membershipName) {
    case "Youth Membership":
      return { stroke: "#18c2c6", text: "#Youth" };
    case "Business Membership":
      return { stroke: "#1c57b9", text: "#Business" };
    default:
      return { stroke: "#544bc1", text: "#Executive" };
  }
}

const formatName = (name: string) => {
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
};

const OBJECT_FIT_COVER = { objectFit: "cover" as const };

function ProfileModal({
  show,
  onHide,
  selectedUser,
  title = DEFAULT_TITLE,
  isConnected,
}: ProfileModalProps) {
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const imageSize = useMemo(
    () =>
      isDesktop
        ? IMAGE_SIZES.desktop
        : isLgTablet
          ? IMAGE_SIZES.lgTablet
          : isMdTablet
            ? IMAGE_SIZES.mdTablet
            : IMAGE_SIZES.mobile,
    [isDesktop, isLgTablet, isMdTablet]
  );

  const fullName = useMemo(() => {
    if (!selectedUser) return "";
    return `${formatName(selectedUser.firstName || "")} ${formatName(selectedUser.lastName || "")}`.trim();
  }, [selectedUser]);

  const membershipInfo = useMemo(() => {
    if (!selectedUser) return "";
    if (selectedUser.membershipName === "Youth Membership") {
      return selectedUser.studyInterest
        ? formatName(selectedUser.studyInterest)
        : "";
    }
    return selectedUser.personalPositionCompany
      ? formatName(selectedUser.personalPositionCompany)
      : "";
  }, [selectedUser]);

  const profileImageUrl = useMemo(
    () =>
      selectedUser?.ProfessionalPhoto ||
      selectedUser?.PersonalProfessionalPhoto ||
      "/user.png",
    [selectedUser?.ProfessionalPhoto, selectedUser?.PersonalProfessionalPhoto]
  );

  const curvedTextProps = useMemo(
    () => getMembershipTheme(selectedUser?.membershipName),
    [selectedUser?.membershipName]
  );

  const bannerStyle = useMemo(
    () => ({
      backgroundImage: selectedUser?.BannerImage
        ? `url(${selectedUser.BannerImage})`
        : undefined,
      backgroundSize: "cover" as const,
      backgroundPosition: "center" as const,
      backgroundRepeat: "no-repeat" as const,
      width: "100%",
      minHeight: "150px",
      aspectRatio: "3 / 1",
    }),
    [selectedUser?.BannerImage]
  );

  const imageContainerStyle = useMemo(
    () => ({ width: imageSize, height: imageSize }),
    [imageSize]
  );

  const isYouthMembership = selectedUser?.membershipName === "Youth Membership";

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      centered
      className="profile-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedUser && (
          <>
            <div className="card mx-auto rounded-3 overflow-hidden border-0 shadow-lg">
              <div
                className={`${
                  selectedUser.BannerImage ? "" : "gradient-bg"
                } position-relative profile-banner d-flex justify-content-center align-items-center`}
                style={bannerStyle}
              >
                {!selectedUser.BannerImage && (
                  <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <img
                      src="/SMBF-logo-white.png"
                      alt="SMBF logo"
                      width={120}
                      height={120}
                      className="w-25 h-25"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="position-absolute translate-middle image-container">
                  <div
                    className="position-relative w-100 h-100"
                    style={imageContainerStyle}
                  >
                    <Image
                      src={profileImageUrl}
                      roundedCircle
                      alt="User profile"
                      width={imageSize}
                      height={imageSize}
                      className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const t = e.target as HTMLImageElement;
                        if (t.src && !t.src.endsWith("/user.png")) t.src = "/user.png";
                      }}
                    />

                    <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                      <CurvedTextOverlay
                        size={imageSize}
                        stroke={curvedTextProps.stroke}
                        text={curvedTextProps.text}
                      />
                    </span>
                  </div>
                </div>
              </div>
              <div className="mx-3 pt-5 pb-2 flex-grow-1">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="mt-md-4 mt-lg-5">
                    <h2 className="mb-0">{fullName}</h2>
                    {membershipInfo && <span>{membershipInfo}</span>}
                    {selectedUser.businessSectorIndustry && (
                      <div className="d-flex gap-1">
                        <span className="text-muted">
                          {selectedUser.businessSectorIndustry}
                        </span>
                      </div>
                    )}
                    {!isConnected && selectedUser.city && (
                      <div className="d-flex gap-1 text-muted align-items-center">
                        <FaMapMarkerAlt /> <span>{selectedUser.city}</span>
                      </div>
                    )}
                  </div>
                  <div className="d-none d-md-block">
                    {isYouthMembership ? (
                      <div className="fw-semibold d-flex gap-1 align-items-center">
                        <FaUniversity size={25} />
                        {selectedUser.universityCollege ?? "Not Provided Yet"}
                      </div>
                    ) : (
                      <div className="d-flex gap-3 align-items-center cursor-pointer">
                        {selectedUser.businessLogo ? (
                          <img
                            src={selectedUser.BusinessLogo}
                            className="rounded-circle"
                            width={40}
                            height={40}
                            alt={selectedUser.businessName ? `${selectedUser.businessName} logo` : "Business logo"}
                            loading="lazy"
                            decoding="async"
                            style={OBJECT_FIT_COVER}
                          />
                        ) : (
                          <MdOutlineBusiness size={30} />
                        )}
                        <div className="fw-semibold">
                          {selectedUser.businessName ?? "Not Provided Yet"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="">
                {isYouthMembership ? (
                  isConnected ? (
                    <div className="px-3">
                      <hr />
                      <Row>
                        <Col md={4} className="mb-3 d-block d-md-none">
                          <div className="d-flex align-items-start">
                            <FaGraduationCap
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>Education Level</strong>
                              <div className="text-muted small">
                                {selectedUser.educationLevel ||
                                  "Not Provided Yet"}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaIdBadge
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>Membership type</strong>
                              <div className="text-muted small">
                                {selectedUser.membershipName ||
                                  "Not Provided Yet"}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaTools
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>Skills or Experience</strong>
                              <div className="text-muted small">
                                {selectedUser.skillsExperience ||
                                  "Not Provided Yet"}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaUsers
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>Interested in Peer Networking</strong>
                              <div className="text-muted small">
                                {selectedUser.peerNetworking ||
                                  "Not Provided Yet"}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaUserCircle
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>About</strong>
                              <div className="text-muted small">
                                {selectedUser.aboutYou || "Not Provided Yet"}
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                    ) : (
                      <div className="p-3 d-block d-md-none">
                        <hr />
                        <h5 className="fw-semibold mb-3">Education Details</h5>
                        <Row>
                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaGraduationCap
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>Education Level</strong>
                                <div className="text-muted small">
                                  {selectedUser.universityCollege ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )
                ) : (
                  <>
                    {isConnected ? (
                      <div className="p-3">
                        <hr className="mt-0" />
                        <h5 className="fw-semibold mb-3">Business Details</h5>
                        <Row>
                          <Col md={4} className="mb-3 d-block d-md-none">
                            <div className="d-flex align-items-start">
                              <FaBuilding
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div className="d-flex justify-content-between w-100">
                                <div>
                                  <strong>Business Name</strong>
                                  <div className="text-muted small">
                                    {selectedUser.businessName ||
                                      "Not Provided Yet"}
                                  </div>
                                </div>
                                {selectedUser.BusinessLogo && (
                                  <img
                                    src={selectedUser.BusinessLogo}
                                    className="rounded-circle me-2 mt-1"
                                    width={40}
                                    height={40}
                                    alt={selectedUser.businessName ? `${selectedUser.businessName} logo` : "Business logo"}
                                    loading="lazy"
                                    decoding="async"
                                    style={OBJECT_FIT_COVER}
                                  />
                                )}
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaIndustry
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>Sector / Industry</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessSectorIndustry ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaGlobe
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>Countries of Operation</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessCountryOperation ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaInfoCircle
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>About the Business</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessAbout ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaLink
                                size={20}
                                className="me-2 mt-1 text-secondary flex-shrink-0"
                              />
                              <div className="d-flex flex-column w-100">
                                <strong>Website URL</strong>
                                <a
                                  href={selectedUser.businessWebsiteUrl || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted small text-truncate pr-1"
                                >
                                  {selectedUser.businessWebsiteUrl ||
                                    "Not Provided Yet"}
                                </a>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaBullseye
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>Objectives</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessObjectives ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaIdBadge
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />

                              <div>
                                <strong>Membership Type</strong>
                                <div className="text-muted small">
                                  {selectedUser.membershipName ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <hr />
                        <h5 className="fw-semibold mb-3">Discounts</h5>
                        <Row>
                          <Col md={6} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaIdBadge
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>Products or Services Offered</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessProductServices ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>
                          <Col md={6} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaUserCheck
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>Discounts for Platform Members</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessDiscount ||
                                    "Not Provided Yet"}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ) : (
                      <div className="p-3 d-block d-md-none">
                        <hr />
                        <h5 className="fw-semibold mb-3">Business Details</h5>
                        <Row>
                          <Col md={4} className="mb-3 ">
                            <div className="d-flex align-items-start">
                              <FaBuilding
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div className="d-flex justify-content-between w-100">
                                <div>
                                  <strong>Business Name</strong>
                                  <div className="text-muted small">
                                    {selectedUser.businessName ||
                                      "Not Provided Yet"}
                                  </div>
                                </div>
                                <img
                                  src={selectedUser.BusinessLogo}
                                  className="rounded-circle me-2 mt-1"
                                  width={40}
                                  height={40}
                                  alt={selectedUser.businessName ? `${selectedUser.businessName} logo` : "Business logo"}
                                  loading="lazy"
                                  decoding="async"
                                  style={OBJECT_FIT_COVER}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

ProfileModal.displayName = "ProfileModal";

export default memo(ProfileModal);
